// Deep Investigation → branded PDF export.
//
// Companion to DeepInvestigationDrawer.tsx. Takes the same structured
// `InvestigationReport` the drawer already renders on-screen and turns it
// into a Kloudynet-branded PDF deliverable, entirely client-side (jsPDF),
// mirroring the visual language already established by incidentReportPdf.ts
// and the shared pdfTemplate.ts primitives.
//
// Note: the overview strip and Timeline/MITRE sections are hand-drawn
// (not jspdf-autotable via drawTable()) - autotable badly mis-rendered
// long free-text cells here (overlapping/garbled rows), so this module
// keeps full manual control over layout instead.
import {
  COLORS,
  CONTENT_W,
  MARGIN_BOTTOM,
  MARGIN_X,
  PAGE_H,
  createReportPdf,
  drawCallout,
  drawClosingFooter,
  drawEmptyState,
  drawSectionBanner,
  ensureSpace,
  finalizeFooters,
  fmtGeneratedAt,
  preloadPdfLogo,
  savePdf,
  type DocCtx,
} from './pdfTemplate';
import { getTz } from './globalTz';

type RGB = [number, number, number];

export interface DeepInvestigationEvidenceCitation { source_table: string; detail: string }
export interface DeepInvestigationTimelineEvent { ts: string; event: string; source_table: string }
export interface DeepInvestigationKqlQuery { purpose: string; query: string; rows_returned?: number }
export interface DeepInvestigationMitreMapping { tactic: string; technique_id: string; technique_name: string; evidence: string }
export interface DeepInvestigationKeyFinding { title: string; detail: string; evidence: DeepInvestigationEvidenceCitation[] }
export interface DeepInvestigationScope {
  users: string[]; hosts: string[]; ips: string[]; files: string[]; processes: string[]; urls: string[];
  blast_radius_summary: string;
}
export interface DeepInvestigationRecommendedActions { immediate: string[]; short_term: string[]; long_term: string[] }

export interface DeepInvestigationReport {
  verdict: string;
  confidence: number;
  severity_assessment: string;
  executive_summary: string;
  timeline: DeepInvestigationTimelineEvent[];
  scope: DeepInvestigationScope;
  mitre_attack: DeepInvestigationMitreMapping[];
  key_findings: DeepInvestigationKeyFinding[];
  recommended_actions: DeepInvestigationRecommendedActions;
  kql_queries_used: DeepInvestigationKqlQuery[];
  confidence_explanation: string;
}

export interface DeepInvestigationReportPayload {
  incidentId: string;
  incidentNumber?: string | null;
  incidentTitle?: string | null;
  customerId: string;
  customerName?: string;
  generatedAt: string;
  aiModel?: string | null;
  promptVersion?: string | null;
  turnsUsed?: number;
  kqlQueriesRun?: number;
  report: DeepInvestigationReport;
}

export interface ExportDeepInvestigationOpts {
  customerName: string;
}

export interface ExportResult { ok: boolean; fileName?: string; error?: string }

function safeFileSlug(s: string): string {
  return s
    .replace(/[^a-zA-Z0-9 _-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[-\s]+|[-\s]+$/g, '')
    .slice(0, 80);
}

// ── Small shared text primitives (paragraph / bullets / pills), self-
// contained so this module doesn't reach into incidentReportPdf.ts's
// private helpers ──
function renderParagraph(ctx: DocCtx, text: string): void {
  const { pdf } = ctx;
  const trimmed = (text ?? '').trim();
  if (!trimmed) return;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9.5);
  pdf.setTextColor(...COLORS.ink);
  const lines: string[] = pdf.splitTextToSize(trimmed, CONTENT_W);
  const lineH = 4.6;
  for (const line of lines) {
    if (ctx.y + lineH > PAGE_H - MARGIN_BOTTOM) {
      pdf.addPage();
      ctx.y = 18;
    }
    pdf.text(String(line), MARGIN_X, ctx.y);
    ctx.y += lineH;
  }
  ctx.y += 2;
}

function renderBulletList(ctx: DocCtx, items: string[]): void {
  if (items.length === 0) return;
  const { pdf } = ctx;
  const lineH = 4.6;
  const indent = 6;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9.5);
  for (const item of items) {
    const lines: string[] = pdf.splitTextToSize(item, CONTENT_W - indent);
    for (let i = 0; i < lines.length; i++) {
      if (ctx.y + lineH > PAGE_H - MARGIN_BOTTOM) {
        pdf.addPage();
        ctx.y = 18;
      }
      if (i === 0) {
        pdf.setFillColor(...COLORS.primary);
        pdf.circle(MARGIN_X + 1.6, ctx.y - 1.2, 0.85, 'F');
      }
      pdf.setTextColor(...COLORS.ink);
      pdf.text(String(lines[i] ?? ''), MARGIN_X + indent, ctx.y);
      ctx.y += lineH;
    }
  }
  ctx.y += 2;
}

function renderLabeledLine(ctx: DocCtx, label: string, value: string): void {
  if (!value.trim()) return;
  const { pdf } = ctx;
  ensureSpace(ctx, 6);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(...COLORS.primaryDark);
  const labelW = pdf.getStringUnitWidth(`${label}: `) * 9 / pdf.internal.scaleFactor;
  pdf.text(`${label}: `, MARGIN_X, ctx.y);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.ink);
  const lines: string[] = pdf.splitTextToSize(value, CONTENT_W - labelW);
  let y = ctx.y;
  lines.forEach((line, i) => {
    if (y + 4.6 > PAGE_H - MARGIN_BOTTOM) {
      pdf.addPage();
      y = 18;
    }
    pdf.text(String(line), i === 0 ? MARGIN_X + labelW : MARGIN_X, y);
    y += 4.6;
  });
  ctx.y = y + 1.5;
}

function renderCodeBlock(ctx: DocCtx, code: string): void {
  const { pdf } = ctx;
  pdf.setFont('courier', 'normal');
  pdf.setFontSize(7.8);
  const lines: string[] = pdf.splitTextToSize(code || '', CONTENT_W - 10);
  const lineH = 3.8;
  const boxH = lines.length * lineH + 6;
  ensureSpace(ctx, Math.min(boxH, PAGE_H - MARGIN_BOTTOM - 20));
  const y = ctx.y;
  const availableH = PAGE_H - MARGIN_BOTTOM - y;
  if (boxH > availableH && availableH < 30) {
    pdf.addPage();
    ctx.y = 18;
  }
  const boxY = ctx.y;
  pdf.setFillColor(...COLORS.panelBg);
  pdf.roundedRect(MARGIN_X, boxY, CONTENT_W, Math.min(boxH, 80), 2, 2, 'F');
  pdf.setTextColor(...COLORS.ink);
  let ly = boxY + 4.5;
  for (const line of lines) {
    if (ly + lineH > PAGE_H - MARGIN_BOTTOM) {
      pdf.addPage();
      ctx.y = 18;
      pdf.setFillColor(...COLORS.panelBg);
      pdf.roundedRect(MARGIN_X, ctx.y, CONTENT_W, Math.min((lines.length) * lineH + 6, 80), 2, 2, 'F');
      ly = ctx.y + 4.5;
    }
    pdf.text(String(line), MARGIN_X + 3, ly);
    ly += lineH;
  }
  ctx.y = ly + 3;
}

/** Pill primitive - mirrors incidentReportPdf.ts's drawPill so badges look consistent across both PDF exports. */
function drawPill(pdf: DocCtx['pdf'], x: number, y: number, label: string, bg: RGB, fg: RGB, fontSize = 8): number {
  const padX = 4;
  const h = 6.2;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(fontSize);
  const textW = pdf.getStringUnitWidth(label) * fontSize / pdf.internal.scaleFactor;
  const w = textW + padX * 2;
  pdf.setFillColor(...bg);
  pdf.roundedRect(x, y, w, h, h / 2, h / 2, 'F');
  pdf.setTextColor(...fg);
  pdf.text(label, x + w / 2, y + h / 2 + 0.4, { align: 'center', baseline: 'middle' });
  return w;
}

function verdictColors(verdict: string): { bg: RGB; fg: RGB } {
  const v = (verdict || '').toUpperCase();
  if (v.includes('TRUE')) return { bg: COLORS.danger, fg: COLORS.white };
  if (v.includes('BENIGN')) return { bg: COLORS.yellow, fg: COLORS.ink };
  if (v.includes('FALSE')) return { bg: COLORS.success, fg: COLORS.white };
  return { bg: COLORS.info, fg: COLORS.white };
}

function severityColors(sev: string): { bg: RGB; fg: RGB } {
  const s = (sev || '').toLowerCase();
  if (s.includes('critical') || s.includes('high')) return { bg: COLORS.danger, fg: COLORS.white };
  if (s.includes('medium')) return { bg: COLORS.warning, fg: COLORS.white };
  if (s.includes('low')) return { bg: COLORS.yellow, fg: COLORS.ink };
  if (s.includes('info')) return { bg: COLORS.info, fg: COLORS.white };
  return { bg: COLORS.riskMinimalBg, fg: COLORS.riskMinimalFg };
}

/** Hand-drawn verdict/severity/confidence/model strip - replaces an earlier
 *  drawTable()-based version that badly mis-rendered (missing header, no
 *  grid, overlapping the banner above it). Full manual control here avoids
 *  relying on autotable for a single-row badge strip. */
function drawOverviewStrip(ctx: DocCtx, r: DeepInvestigationReport, payload: DeepInvestigationReportPayload): void {
  const { pdf } = ctx;
  const h = 16;
  ensureSpace(ctx, h + 6);
  const x = MARGIN_X;
  const y = ctx.y;

  pdf.setFillColor(...COLORS.panelBg);
  pdf.roundedRect(x, y, CONTENT_W, h, 2, 2, 'F');

  let px = x + 5;
  const py = y + (h - 6.2) / 2;
  const vTone = verdictColors(r.verdict);
  px += drawPill(pdf, px, py, r.verdict, vTone.bg, vTone.fg) + 4;
  const sTone = severityColors(r.severity_assessment);
  px += drawPill(pdf, px, py, (r.severity_assessment || '').toUpperCase(), sTone.bg, sTone.fg) + 6;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(...COLORS.ink);
  const kqlCount = payload.kqlQueriesRun ?? r.kql_queries_used?.length ?? undefined;
  const metaParts = [
    `Confidence ${r.confidence}%`,
    kqlCount !== undefined ? `${kqlCount} KQL queries` : undefined,
  ].filter(Boolean);
  pdf.text(metaParts.join('  ·  '), px, y + h / 2 + 1.2, { baseline: 'middle' });

  ctx.y = y + h + 6;
}

/** Manual per-event Timeline renderer - see file header note on why this
 *  isn't a drawTable() call. */
function renderTimeline(ctx: DocCtx, events: DeepInvestigationTimelineEvent[]): void {
  const { pdf } = ctx;
  events.forEach((t, i) => {
    ensureSpace(ctx, 12);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...COLORS.primaryDark);
    pdf.text(fmtGeneratedAt(t.ts, getTz()), MARGIN_X, ctx.y);
    if (t.source_table) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7.5);
      pdf.setTextColor(...COLORS.muted);
      pdf.text(t.source_table, MARGIN_X + CONTENT_W, ctx.y, { align: 'right' });
    }
    ctx.y += 4.6;
    renderParagraph(ctx, t.event);
    if (i < events.length - 1) {
      ensureSpace(ctx, 3);
      pdf.setDrawColor(...COLORS.rule);
      pdf.setLineWidth(0.15);
      pdf.line(MARGIN_X, ctx.y - 1, MARGIN_X + CONTENT_W, ctx.y - 1);
      ctx.y += 2;
    }
  });
}

/** Manual per-mapping MITRE ATT&CK renderer - same rationale as the
 *  timeline: avoids autotable for cells holding long free-text evidence. */
function renderMitreMappings(ctx: DocCtx, mappings: DeepInvestigationMitreMapping[]): void {
  const { pdf } = ctx;
  mappings.forEach((m) => {
    ensureSpace(ctx, 10);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(...COLORS.primaryDark);
    pdf.text(`${m.tactic} - ${m.technique_id} ${m.technique_name}`, MARGIN_X, ctx.y);
    ctx.y += 5;
    renderParagraph(ctx, m.evidence);
  });
}

export async function exportDeepInvestigationReportPdf(
  payload: DeepInvestigationReportPayload,
  opts: ExportDeepInvestigationOpts,
): Promise<ExportResult> {
  try {
    await preloadPdfLogo();
    const r = payload.report;
    const ctx = createReportPdf({
      title: 'Deep Investigation Report',
      customerName: opts.customerName,
      periodLabel: payload.incidentTitle
        ? `Incident ${payload.incidentNumber ?? payload.incidentId} · ${payload.incidentTitle}`
        : `Incident ${payload.incidentNumber ?? payload.incidentId}`,
      generatedLabel: fmtGeneratedAt(payload.generatedAt, getTz()),
    });

    // ── Overview strip: verdict / severity / confidence / model ──────────
    drawOverviewStrip(ctx, r, payload);

    // ── Executive Summary ─────────────────────────────────────────────────
    drawSectionBanner(ctx, 'Executive Summary');
    if (r.executive_summary?.trim()) renderParagraph(ctx, r.executive_summary);
    else drawEmptyState(ctx, 'No executive summary was returned for this investigation.');

    // ── Timeline ───────────────────────────────────────────────────────────
    drawSectionBanner(ctx, 'Timeline');
    if (r.timeline?.length) renderTimeline(ctx, r.timeline);
    else drawEmptyState(ctx, 'No timeline events were reconstructed.');

    // ── Scope / blast radius ───────────────────────────────────────────────
    drawSectionBanner(ctx, 'Scope');
    const scopeGroups: Array<[string, string[]]> = [
      ['Users', r.scope?.users ?? []],
      ['Hosts', r.scope?.hosts ?? []],
      ['IPs', r.scope?.ips ?? []],
      ['Files', r.scope?.files ?? []],
      ['Processes', r.scope?.processes ?? []],
      ['URLs', r.scope?.urls ?? []],
    ];
    const nonEmptyGroups = scopeGroups.filter(([, values]) => values.length > 0);
    if (nonEmptyGroups.length === 0 && !r.scope?.blast_radius_summary?.trim()) {
      drawEmptyState(ctx, 'No scope data was captured for this investigation.');
    } else {
      for (const [label, values] of nonEmptyGroups) renderLabeledLine(ctx, label, values.join(', '));
      if (r.scope?.blast_radius_summary?.trim()) {
        ctx.y += 1;
        renderLabeledLine(ctx, 'Blast Radius', r.scope.blast_radius_summary);
      }
    }

    // ── MITRE ATT&CK Mapping ───────────────────────────────────────────────
    drawSectionBanner(ctx, 'MITRE ATT&CK Mapping');
    if (r.mitre_attack?.length) renderMitreMappings(ctx, r.mitre_attack);
    else drawEmptyState(ctx, 'No MITRE ATT&CK techniques could be asserted from the evidence.');

    // ── Key Findings ────────────────────────────────────────────────────────
    drawSectionBanner(ctx, 'Key Findings');
    if (r.key_findings?.length) {
      r.key_findings.forEach((f) => {
        ensureSpace(ctx, 10);
        const { pdf } = ctx;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9.8);
        pdf.setTextColor(...COLORS.primaryDark);
        pdf.text(f.title, MARGIN_X, ctx.y);
        ctx.y += 5;
        renderParagraph(ctx, f.detail);
        if (f.evidence?.length) {
          renderBulletList(ctx, f.evidence.map((e) => `${e.source_table}: ${e.detail}`));
        }
      });
    } else {
      drawEmptyState(ctx, 'No key findings were reported.');
    }

    // ── Recommended Actions ─────────────────────────────────────────────────
    drawSectionBanner(ctx, 'Recommended Actions', 'orange');
    const actionGroups: Array<[string, string[], 'critical' | 'warning' | 'info']> = [
      ['Immediate', r.recommended_actions?.immediate ?? [], 'critical'],
      ['Short Term', r.recommended_actions?.short_term ?? [], 'warning'],
      ['Long Term', r.recommended_actions?.long_term ?? [], 'info'],
    ];
    const anyActions = actionGroups.some(([, items]) => items.length > 0);
    if (anyActions) {
      for (const [label, items, tone] of actionGroups) {
        if (items.length === 0) continue;
        drawCallout(ctx, { title: label, body: items.map((i) => `• ${i}`).join('\n'), tone });
      }
    } else {
      drawEmptyState(ctx, 'No recommended actions were returned.');
    }

    // ── KQL Queries Used ─────────────────────────────────────────────────────
    drawSectionBanner(ctx, 'KQL Queries Used');
    if (r.kql_queries_used?.length) {
      r.kql_queries_used.forEach((q) => {
        ensureSpace(ctx, 10);
        const { pdf } = ctx;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(...COLORS.primaryDark);
        pdf.text(q.purpose, MARGIN_X, ctx.y);
        if (typeof q.rows_returned === 'number') {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.setTextColor(...COLORS.muted);
          pdf.text(`${q.rows_returned} row${q.rows_returned === 1 ? '' : 's'}`, MARGIN_X + CONTENT_W, ctx.y, { align: 'right' });
        }
        ctx.y += 5;
        renderCodeBlock(ctx, q.query);
      });
    } else {
      drawEmptyState(ctx, 'No KQL queries were recorded for this investigation.');
    }

    // ── Confidence Explanation ───────────────────────────────────────────────
    drawSectionBanner(ctx, 'Confidence Explanation');
    if (r.confidence_explanation?.trim()) renderParagraph(ctx, r.confidence_explanation);
    else drawEmptyState(ctx, 'No confidence explanation was returned.');

    drawClosingFooter(ctx);
    finalizeFooters(ctx);

    const titleSrc = (payload.incidentTitle ?? '').trim();
    const numberSrc = (payload.incidentNumber ?? payload.incidentId ?? '').toString().trim();
    const titleSlug = safeFileSlug(titleSrc);
    const numberSlug = safeFileSlug(numberSrc);
    const fileBase =
      titleSlug && numberSlug ? `Deep Investigation - ${titleSlug} - ${numberSlug}`
      : numberSlug ? `Deep Investigation - ${numberSlug}`
      : 'deep-investigation-report';
    const fileName = savePdf(ctx, fileBase);
    return { ok: true, fileName };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
